import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  PawPrint,
  Building2,
  FileText,
  Truck,
  Stethoscope,
  ShieldCheck,
  BookOpen,
  Settings,
  CreditCard,
  UserCog,
  LayoutDashboard,
  Building,
  Pill,
  Layers,
  Scale,
  FileDown,
  ArrowRightLeft,
  UserCircle,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Box,
  UtensilsCrossed,
  Shield,
  Home
} from "lucide-react";
import { useIsAdmin } from "@/hooks/use-user-role";
import { useTestUserRestrictions } from "@/hooks/use-test-user";
import { useCollectorAccess, useCollectorStatus } from "@/hooks/use-collector-access";
import { useFacilityAccess } from "@/hooks/use-facility-access";
import { useUiPreferences } from "@/hooks/use-ui-preferences";
import { useTransportAccess } from "@/hooks/use-transport-access";
import { useAdminViewContext } from "@/context/AdminViewContext";
import { cn } from "@/lib/utils";

// Default nav state for groups
const DEFAULT_NAV_STATE = {
  facility_open: true,
  collector_open: true,
  general_open: true,
  admin_open: true,
};

// Facility Management navigation structure
const facilityNavStructure = {
  dashboard: { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  facilities: { to: "/admin/facilities", label: "Facilities", icon: Building2 },
  enclosures: { to: "/admin/enclosures", label: "Enclosures", icon: Home },
  animals: {
    label: "Animals",
    icon: PawPrint,
    items: [
      { to: "/admin/animals", label: "Animals", icon: PawPrint },
      { to: "/admin/animal-groups", label: "Animal Groups", icon: Layers },
      { to: "/admin/vet-records", label: "Vet Records", icon: Stethoscope },
      { to: "/admin/enrichment-diet-plans", label: "Enrichment & Diet Plans", icon: UtensilsCrossed },
    ]
  },
  permits: {
    label: "Permits & Compliance",
    icon: FileText,
    items: [
      { to: "/admin/permits", label: "Permits", icon: FileText },
      { to: "/admin/inspections", label: "Inspections", icon: ShieldCheck },
      { to: "/admin/regulations", label: "Regulations", icon: Scale },
    ]
  },
  formulary: { to: "/admin/formulary", label: "Formulary", icon: Pill },
  transportation: { to: "/admin/transport", label: "Transportation", icon: Truck },
};

// Collector Tools navigation structure
const collectorNavStructure = {
  dashboard: { to: "/admin/collector/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  collection: { to: "/admin/collector/animals", label: "My Collection", icon: PawPrint },
  enclosures: { to: "/admin/collector/enclosures", label: "Enclosures", icon: Home },
  analytics: { to: "/admin/collector/analytics", label: "Analytics", icon: Shield },
};

// General navigation structure
const generalNavStructure = {
  transfers: { to: "/admin/transfers", label: "Transfers", icon: ArrowRightLeft },
  exports: { to: "/admin/exports", label: "Exports & Reports", icon: FileDown },
  legal: { to: "/admin/legal-lookup", label: "Legal Lookup", icon: BookOpen },
  uiSettings: { to: "/admin/settings/ui", label: "UI Settings", icon: Settings },
  billing: { to: "/admin/billing", label: "Billing", icon: CreditCard },
  users: { to: "/admin/users", label: "Users", icon: UserCog },
  publicProfile: { to: "/admin/public-profile", label: "Public Profile", icon: UserCircle },
};

// Admin-only items
const adminNavItems = [
  { to: "/admin/organizations", label: "Organizations", icon: Building },
  { to: "/admin/security", label: "Security", icon: Shield },
  { to: "/admin/audit", label: "Audit Logs", icon: ClipboardList },
  { to: "/admin/legality-audit", label: "Legality Audit", icon: FileText },
  { to: "/admin/site-settings", label: "Site Settings", icon: Settings },
  { to: "/admin/transport-directory", label: "Transport Directory", icon: Truck },
];

const AdminSidebar = () => {
  const { isAdmin, isSiteAdmin, hasAdminAccess } = useIsAdmin();
  const { isTestUser } = useTestUserRestrictions();
  const { data: hasCollectorAccess } = useCollectorAccess();
  const { hasFacilityAccess } = useFacilityAccess();
  const { isCollectorOnly } = useCollectorStatus();
  const { canViewTransportNav } = useTransportAccess();
  const { isSimulating, simulatedAccess } = useAdminViewContext();
  const { state } = useSidebar();
  const location = useLocation();
  const currentPath = location.pathname;
  const { prefs, updatePrefs } = useUiPreferences();

  // Local state for group open/closed
  const [navState, setNavState] = useState(DEFAULT_NAV_STATE);
  
  // Subgroup state (in-memory only)
  const [animalsSubgroupOpen, setAnimalsSubgroupOpen] = useState(false);
  const [permitsSubgroupOpen, setPermitsSubgroupOpen] = useState(false);
  
  // Track previous path to only auto-expand on route changes
  const prevPathRef = useRef(currentPath);

  // Load nav state from preferences on mount
  useEffect(() => {
    if (prefs?.nav_state) {
      setNavState({ ...DEFAULT_NAV_STATE, ...prefs.nav_state });
    }
  }, [prefs]);

  // Auto-expand groups/subgroups based on current route (only on route changes)
  useEffect(() => {
    const path = currentPath;
    
    // Only auto-expand if the route actually changed
    if (path === prevPathRef.current) {
      return;
    }
    
    prevPathRef.current = path;
    
    // Check if current route is in facility management
    if (path.startsWith('/admin') && !path.startsWith('/admin/collector') && 
        !path.startsWith('/admin/transfers') && !path.startsWith('/admin/exports') &&
        !path.startsWith('/admin/legal-lookup') && !path.startsWith('/admin/settings/ui') &&
        !path.startsWith('/admin/billing') && !path.startsWith('/admin/users') &&
        !path.startsWith('/admin/organizations') && !path.startsWith('/admin/site-settings') &&
        !path.startsWith('/admin/security') && !path.startsWith('/admin/legality-audit')) {
      
      // Auto-expand facility group if route is inside it
      setNavState(prev => ({ ...prev, facility_open: true }));
      
      // Auto-expand Animals subgroup
      if (path.includes('/animals') || path.includes('/animal-groups') || 
          path.includes('/vet-records') || path.includes('/enrichment-diet-plans')) {
        setAnimalsSubgroupOpen(true);
      }
      
      // Auto-expand Permits subgroup
      if (path.includes('/permits') || path.includes('/inspections') || path.includes('/regulations')) {
        setPermitsSubgroupOpen(true);
      }
    }
    
    // Auto-expand collector group
    if (path.startsWith('/admin/collector')) {
      setNavState(prev => ({ ...prev, collector_open: true }));
    }
    
    // Auto-expand general group
    if (path.startsWith('/admin/transfers') || path.startsWith('/admin/exports') ||
        path.startsWith('/admin/legal-lookup') || path.startsWith('/admin/settings/ui') ||
        path.startsWith('/admin/billing') || path.startsWith('/admin/users')) {
      setNavState(prev => ({ ...prev, general_open: true }));
    }
    
    // Auto-expand admin group
    if (path.startsWith('/admin/organizations') || path.startsWith('/admin/site-settings') ||
        path.startsWith('/admin/security') || path.startsWith('/admin/legality-audit')) {
      setNavState(prev => ({ ...prev, admin_open: true }));
    }
  }, [currentPath]);

  // Determine which navigation sections to show based on plan type and role
  // When simulating, use simulated access; otherwise use actual access
  const showFacilityManagement = isSimulating 
    ? simulatedAccess.hasFacilityAccess 
    : (!isCollectorOnly || isSiteAdmin);
  const showCollectorTools = isSimulating 
    ? simulatedAccess.hasCollectorAccess 
    : (hasCollectorAccess || isAdmin);
  const showTransportNav = isSimulating 
    ? simulatedAccess.isSiteAdmin 
    : canViewTransportNav;
  const effectiveSiteAdmin = isSimulating ? simulatedAccess.isSiteAdmin : isSiteAdmin;

  // Helper to check if a route is active
  const isActive = (to: string, end?: boolean) => {
    if (end) {
      return currentPath === to;
    }
    return currentPath.startsWith(to);
  };

  // Helper to build nav link class
  const getNavClass = (to: string, end?: boolean) => {
    const active = isActive(to, end);
    return cn(
      "w-full justify-start",
      active ? "bg-secondary text-secondary-foreground font-medium" : "hover:bg-muted/50"
    );
  };

  // Toggle group and persist to backend
  const toggleGroup = (groupKey: 'facility_open' | 'collector_open' | 'general_open' | 'admin_open') => {
    const newState = { ...navState, [groupKey]: !navState[groupKey] };
    setNavState(newState);
    
    // Persist to backend
    updatePrefs({ nav_state: newState });
  };

  // Render a nav item
  const renderNavItem = (item: any, depth = 0) => {
    const Icon = item.icon;
    const paddingClass = depth === 1 ? "pl-6" : depth === 2 ? "pl-10" : "";
    
    return (
      <SidebarMenuItem key={item.to}>
        <SidebarMenuButton asChild className={cn(getNavClass(item.to, item.end), paddingClass)}>
          <NavLink to={item.to} end={item.end}>
            <Icon className="h-4 w-4" />
            <span>{item.label}</span>
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  // Render a subgroup (Animals, Permits & Compliance)
  const renderSubgroup = (
    subgroup: any, 
    isOpen: boolean, 
    onToggle: () => void,
    depth = 1
  ) => {
    const Icon = subgroup.icon;
    
    return (
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <SidebarMenuItem>
            <SidebarMenuButton className="w-full justify-start hover:bg-muted/30">
              <Icon className="h-4 w-4" />
              <span className="text-sm">{subgroup.label}</span>
              {isOpen ? (
                <ChevronDown className="ml-auto h-4 w-4" />
              ) : (
                <ChevronRight className="ml-auto h-4 w-4" />
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {subgroup.items.map((item: any) => renderNavItem(item, depth + 1))}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <PawPrint className="h-4 w-4" />
          </div>
          {state === "expanded" && (
            <div>
              <NavLink to="/" className="hover:opacity-80 transition-opacity">
                <h1 className="text-lg font-semibold">Zoocertica</h1>
              </NavLink>
              <p className="text-xs text-muted-foreground">Admin Console</p>
            </div>
          )}
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        {/* Facility Management Section */}
        {showFacilityManagement && (
          <Collapsible
            open={navState.facility_open}
            onOpenChange={() => toggleGroup('facility_open')}
          >
            <SidebarGroup>
              <CollapsibleTrigger asChild>
                <SidebarGroupLabel className="cursor-pointer hover:bg-muted/50 rounded-md px-2 py-1.5 flex items-center">
                  <ClipboardList className="mr-2 h-4 w-4" />
                  <span className="font-semibold">Facility Management</span>
                  {navState.facility_open ? (
                    <ChevronDown className="ml-auto h-4 w-4" />
                  ) : (
                    <ChevronRight className="ml-auto h-4 w-4" />
                  )}
                </SidebarGroupLabel>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {renderNavItem(facilityNavStructure.dashboard)}
                    {renderNavItem(facilityNavStructure.facilities)}
                    {renderNavItem(facilityNavStructure.enclosures)}
                    {renderSubgroup(
                      facilityNavStructure.animals,
                      animalsSubgroupOpen,
                      () => setAnimalsSubgroupOpen(!animalsSubgroupOpen)
                    )}
                    {renderSubgroup(
                      facilityNavStructure.permits,
                      permitsSubgroupOpen,
                      () => setPermitsSubgroupOpen(!permitsSubgroupOpen)
                    )}
                    {renderNavItem(facilityNavStructure.formulary)}
                    {showTransportNav && renderNavItem(facilityNavStructure.transportation)}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}

        {/* Collector Tools Section */}
        {showCollectorTools && (
          <Collapsible
            open={navState.collector_open}
            onOpenChange={() => toggleGroup('collector_open')}
          >
            <SidebarGroup>
              <CollapsibleTrigger asChild>
                <SidebarGroupLabel className="cursor-pointer hover:bg-muted/50 rounded-md px-2 py-1.5 flex items-center">
                  <Box className="mr-2 h-4 w-4" />
                  <span className="font-semibold">Collector Tools</span>
                  {navState.collector_open ? (
                    <ChevronDown className="ml-auto h-4 w-4" />
                  ) : (
                    <ChevronRight className="ml-auto h-4 w-4" />
                  )}
                </SidebarGroupLabel>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {renderNavItem(collectorNavStructure.dashboard)}
                    {renderNavItem(collectorNavStructure.collection)}
                    {renderNavItem(collectorNavStructure.enclosures)}
                    {renderNavItem(collectorNavStructure.analytics)}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}

        {/* General Section */}
        <Collapsible
          open={navState.general_open}
          onOpenChange={() => toggleGroup('general_open')}
        >
          <SidebarGroup>
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className="cursor-pointer hover:bg-muted/50 rounded-md px-2 py-1.5 flex items-center">
                <Settings className="mr-2 h-4 w-4" />
                <span className="font-semibold">General</span>
                {navState.general_open ? (
                  <ChevronDown className="ml-auto h-4 w-4" />
                ) : (
                  <ChevronRight className="ml-auto h-4 w-4" />
                )}
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {renderNavItem(generalNavStructure.transfers)}
                  {renderNavItem(generalNavStructure.exports)}
                  {renderNavItem(generalNavStructure.legal)}
                  {renderNavItem(generalNavStructure.uiSettings)}
                  {!isTestUser && renderNavItem(generalNavStructure.billing)}
                  {hasAdminAccess && renderNavItem(generalNavStructure.users)}
                  {(hasCollectorAccess || hasFacilityAccess || isAdmin) && renderNavItem(generalNavStructure.publicProfile)}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        {/* Administration Section (site admin only) */}
        {effectiveSiteAdmin && (
          <Collapsible
            open={navState.admin_open}
            onOpenChange={() => toggleGroup('admin_open')}
          >
            <SidebarGroup>
              <CollapsibleTrigger asChild>
                <SidebarGroupLabel className="cursor-pointer hover:bg-muted/50 rounded-md px-2 py-1.5 flex items-center">
                  <Shield className="mr-2 h-4 w-4" />
                  <span className="font-semibold">Administration</span>
                  {navState.admin_open ? (
                    <ChevronDown className="ml-auto h-4 w-4" />
                  ) : (
                    <ChevronRight className="ml-auto h-4 w-4" />
                  )}
                </SidebarGroupLabel>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {adminNavItems.map((item) => renderNavItem(item))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}
      </SidebarContent>

      {state === "expanded" && (
        <SidebarFooter className="p-4">
          <p className="text-xs text-muted-foreground text-center">
            © {new Date().getFullYear()} Zoocertica
          </p>
        </SidebarFooter>
      )}
    </Sidebar>
  );
};

export default AdminSidebar;
